import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { TypeConstantService } from 'src/app/services/type-constant.service';
import { DestinationEndpoint } from 'src/app/models/destination-endpoint';
import { UntypedFormControl, ReactiveFormsModule } from '@angular/forms';
import { ToastService } from 'src/app/services/toast.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { LTSpinnerComponent } from 'src/app/features/lt-shared-ui/lt-info/components/lt-spinner/lt-spinner.component';
import { CommonModule } from '@angular/common';
@Component({
  selector: 'app-destinations-dropdown-view',
  templateUrl: './destinations-dropdown-view.component.html',
  styleUrls: ['./destinations-dropdown-view.component.scss'],
  providers: [TypeConstantService],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatAutocompleteModule,
    MatOptionModule,
    LTSpinnerComponent,
    CommonModule,
  ],
})
export class DestinationsDropdownViewComponent implements OnInit, OnDestroy, OnChanges {
  componentDestroyed$ = new Subject<boolean>();
  
  @Input() destinations: DestinationEndpoint[];
  @Input() destinationStrs: string[] = [];
  @Output() itemSelected = new EventEmitter<any>();
  @Output() loadingFailed = new EventEmitter<any>();
  @Input() label = 'Destinations';

  @Input() overrideEndpointId: number = null;
  @Input() overrideEndpointName: string = null;
  @Output() itemSelectedFromOverride = new EventEmitter<any>();

  allDestinations: DestinationEndpoint[];
  public selectedItem: DestinationEndpoint;
  destinationsFiltered: Observable<DestinationEndpoint[]>;
  destinationFormControl = new UntypedFormControl();
  hasDestinations = false;

  constructor(
    protected typeService: TypeConstantService,
    protected toastService: ToastService,
  ) { }

  ngOnInit(): void {
    if (this.destinations != null)
    {
      this.loadDestinations(this.destinations);
    }
    else {
      this.getDestinations();
    }

  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.overrideEndpointId != null && changes?.overrideEndpointId?.currentValue != null) {
      if (changes?.overrideEndpointId?.currentValue != null && changes?.overrideEndpointId.currentValue != undefined) {
        if (this.hasDestinations && this.allDestinations != null) {
          const d = this.allDestinations.find(x => x.destinationEndpointId == this.overrideEndpointId);
          if (d != null) {
            this.selectDestinationForOverride(d);
          }
        }
        else {
          this.getDestinations(this.overrideEndpointId);
        }
      }
    }
    if (changes?.overrideEndpointName != null && changes?.overrideEndpointName?.currentValue != null) {
      if (changes?.overrideEndpointName?.currentValue != null && changes?.overrideEndpointName.currentValue != undefined) {
        if (this.hasDestinations && this.allDestinations != null) {
          const d = this.allDestinations.find(x => x.endpointName == this.overrideEndpointName);
          if (d != null) {
            this.selectDestinationForOverride(d);
          }
        }
        else {
          this.getDestinations(null, this.overrideEndpointName);
        }
      }
    }
    if (changes?.destinationStrs != null && changes?.destinationStrs?.currentValue != null){
      if (this.allDestinations != null && this.hasDestinations)
      {
        if (this.destinationStrs.length == 1)
        {
          const d = this.allDestinations.find(x => x.endpointName == this.destinationStrs[0]);
          if (d != null) {
            this.selectDestinationForOverride(d);
          }
          else {
            this.loadDestinations(this.allDestinations);
          }
        }
        else {
          this.loadDestinations(this.allDestinations);
        }
      }
    }
  }

  clearWithoutReturn(): void {
    this.selectedItem = null;
    this.destinationFormControl.setValue('');
  }

  getDestinations(x: number = null, n: string = null): void {
    this.typeService.getAllDestinationEndpoints()
    .pipe(takeUntil(this.componentDestroyed$))
    .subscribe({
        next: (r) => {
      this.loadDestinations(r, x, n);
      this.hasDestinations = true;
    },
              error: (_error) => {
      this.loadingFailed.emit(true);
      this.hasDestinations = true;
      this.toastService.toastCreate('Failed to load destinations.', 'Danger', {
        autoClose: false,
      });
    },
  });
  }

  loadDestinations(destinations: DestinationEndpoint[], x: number = null, n: string = null): void {
    this.loadingFailed.emit(false);
      this.allDestinations = destinations;
      if (this.destinationStrs != null && this.destinationStrs.length > 0)
      {
        this.allDestinations = this.allDestinations.filter(x => this.destinationStrs.includes(x.endpointName));
        if (this.allDestinations.length == 1)
        {
          this.selectDestinationForOverride(this.allDestinations[0]);
        }
      }

      this.destinationsFiltered = this.destinationFormControl.valueChanges.pipe(
        startWith(''),
        map(value => typeof value === 'string' ? value : value.fullName),
        map(name => name ? this.filterDestinations(name) : this.allDestinations.slice()),
      );

      if (x != null && x != undefined) {
        const d = this.allDestinations.find(x => x.destinationEndpointId == this.overrideEndpointId);
        this.selectDestinationForOverride(d);
      }

      if (n != null && n != undefined) {
        const d = this.allDestinations.find(x => x.endpointName == this.overrideEndpointName);
        this.selectDestinationForOverride(d);
      }
      
      this.hasDestinations = true;
  }

  selectDestination(c: DestinationEndpoint): void {
    this.selectedItem = c;
    this.itemSelected.emit(this.selectedItem);
  }

  selectDestinationForOverride(c: DestinationEndpoint): void {
    this.selectedItem = c;
    this.itemSelectedFromOverride.emit(this.selectedItem);
  }

  displayFnDestination(x: DestinationEndpoint): string {
    return x && x.endpointName ? x.endpointName : '';
  }

  private filterDestinations(name: string): DestinationEndpoint[] {
    const filterValue = name.toLowerCase();
    return this.allDestinations.filter(option => option.endpointName.toLowerCase().includes(filterValue));
  }

  editDestination(): void {
    this.selectedItem = null;
    this.destinationFormControl.setValue('');
    this.itemSelected.emit('');
  }

}
